import type {
  TradeOption,
  TradeSelectionMeta,
} from "@simplybusiness/mobius-trade-selector";
import { useEffect, useState } from "react";
import type { AmbiguousTradeModalProps } from "./AmbiguousTradeModal";
import { findAmbiguousTradePair } from "./tradePairs";
import type { AmbiguousTradePair } from "./types";

type CommitTrade<TMeta extends TradeSelectionMeta | undefined> = (
  trade: TradeOption,
  meta: TMeta,
) => void;

type UseAmbiguousTradeInterceptorResult<
  TMeta extends TradeSelectionMeta | undefined,
> = {
  handleSelectTrade: (trade: TradeOption, meta: TMeta) => void;
  modalProps: AmbiguousTradeModalProps;
};

export const useAmbiguousTradeInterceptor = <
  TMeta extends TradeSelectionMeta | undefined = TradeSelectionMeta | undefined,
>(
  commitTrade: CommitTrade<TMeta>,
): UseAmbiguousTradeInterceptorResult<TMeta> => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingTrade, setPendingTrade] = useState<{
    trade: TradeOption;
    meta: TMeta;
    tradePair: AmbiguousTradePair;
  } | null>(null);

  // Clear pending trade when modal is dismissed
  useEffect(() => {
    if (!isOpen) {
      setPendingTrade(null);
    }
  }, [isOpen]);

  const handleSelectTrade = (trade: TradeOption, meta: TMeta) => {
    const tradePair = findAmbiguousTradePair(trade.value);
    if (tradePair) {
      setPendingTrade({ trade, meta, tradePair });
      setIsOpen(true);
      return;
    }
    commitTrade(trade, meta);
  };

  const handleConfirmTrade = (confirmedTrade?: string) => {
    if (confirmedTrade && pendingTrade) {
      const definition =
        confirmedTrade === pendingTrade.tradePair.selectedTrade
          ? pendingTrade.trade.definition
          : pendingTrade.tradePair.alternativeTradeDefinition;
      commitTrade(
        {
          value: confirmedTrade,
          label: confirmedTrade,
          vertical: pendingTrade.trade.vertical,
          verticalsWithoutRedirect: pendingTrade.trade.verticalsWithoutRedirect,
          definition,
        },
        pendingTrade.meta,
      );
    }
    setIsOpen(false);
  };

  return {
    handleSelectTrade,
    modalProps: {
      isOpen,
      onClose: () => setIsOpen(false),
      confirmTrade: handleConfirmTrade,
      tradePair: pendingTrade?.tradePair,
    },
  };
};
